import _ from "lodash";

import Dimensions from "../../../constants/styles/dimensions";
import { createFromJS } from "../../../types";
import { timelineViewSettingsActionTypes } from "../../definition/timelineViewSettings";
import { actionTypes } from "../actions";
import { appStateSelectors } from "../index";
import {
  addView,
  changeZoomLimits,
  changeZoomSlider,
  clearTimelineGroups,
  getFirstViewIdFromLayout,
  getHorizontalSiblingViewIds,
  getMaxLayoutHeight,
  getReplacingViewId,
  isViewCollapsed,
  moveOutlineColumn,
  moveSpreadsheetColumn,
  moveTimelineScrollPositions,
  recordScrollAndZoom,
  replaceViewsInConditions,
  resizeOutlineColumn,
  resizeSpreadsheetColumn,
  setLayoutTwoViewStackThirds,
  setTwoViewsLayout,
  sortSpreadsheetColumn,
  switchViewType,
  toggleOutlineColumn,
  toggleRelationshipColumn,
  toggleSpreadsheetColumn,
  toggleSubwayThread,
  toggleTimelineGroup
} from "./byId.shared";
import defaultScrollPositionToTimestamp from "./defaultScrollPositionToTimestamp";

const byIdReadonly = (state = createFromJS.AppStateRecord(), action, prevGlobalState) => {
  const activeViewId = appStateSelectors.getActiveViewId(prevGlobalState);
  const activeView = appStateSelectors.getOpenViewsById(prevGlobalState).get(activeViewId);
  const targetViewId = getReplacingViewId(action.viewId, prevGlobalState);

  switch (action.type) {
    case actionTypes.REPLACE_OPEN_VIEWS:
      return replaceViewsInConditions(state, action.openViews, prevGlobalState);
    case actionTypes.ADD_VIEW: {
      // copy the contents of the active view into the new view.
      return addView(state, action.viewId, activeView);
    }
    case actionTypes.SPLIT_VIEW_HORIZONTAL:
    case actionTypes.SPLIT_VIEW_VERTICAL: {
      const splittingView = appStateSelectors.getOpenViewsById(prevGlobalState).get(action.splittingViewId);
      // copy the contents of the previous view into the new view,
      // changing its viewId and size...
      return state.set(
        action.viewId,
        splittingView.merge({
          viewId: action.viewId,
          size: createFromJS.SizeRecord({
            width: 0,
            height: 0
          })
        })
      );
    }
    case actionTypes.MINIMIZE_VIEW: {
      let newState = state;
      const rootLayout = appStateSelectors.getRootLayout(prevGlobalState);

      const targetViewHeight = state.get(targetViewId).size.height;
      let targetHeight = Dimensions.MIN_WORKVIEW_HEIGHT + 1;
      if (targetViewHeight <= Dimensions.COLLAPSE_WORKVIEW_HEIGHT) {
        // To expand
        const siblingViews = getHorizontalSiblingViewIds(targetViewId, rootLayout);
        if (siblingViews !== null) {
          // Find the siblings and the tallest height
          // make the target view being one pixel taller than the tallest
          const siblingHeight = Math.max(...siblingViews.map(vl => getMaxLayoutHeight(vl, state)).toArray());
          targetHeight = siblingHeight + 1;
        }
      } else {
        // To minimize
        // If all siblings are collapsed, need to raise a neighbor
        const siblingViews = getHorizontalSiblingViewIds(targetViewId, rootLayout);
        if (siblingViews !== null && siblingViews.every(v => v === targetViewId || isViewCollapsed(v, state))) {
          const targetViewIndex = siblingViews.findIndex(v => v === targetViewId);
          // neighbor would be the next view
          // or the previous one when the target view is the last
          const neighborIndex = targetViewIndex + (targetViewIndex === siblingViews.count() - 1 ? -1 : 1);
          const neighbor = getFirstViewIdFromLayout(siblingViews.get(neighborIndex));
          newState = newState.setIn([neighbor, "size", "height"], Dimensions.MIN_WORKVIEW_HEIGHT);
        }
        targetHeight = Dimensions.COLLAPSE_WORKVIEW_HEIGHT;
      }
      newState = newState.update(targetViewId, v => v.update("size", size => size.set("height", targetHeight)));
      return newState;
    }
    case actionTypes.POPOUT_VIEW:
    case actionTypes.REMOVE_VIEW: {
      // Ensure it is not the last item, and otherwise delete it.
      return state.count() > 1 ? state.delete(targetViewId) : state;
    }
    case actionTypes.REMOVE_OTHER_VIEWS:
      return state.filter((_v, vId) => vId === targetViewId);
    case actionTypes.RESIZE_VIEWS: {
      return state.withMutations(s => {
        _.each(action.viewChanges, (change, viewId) => {
          s.setIn(
            [viewId, "size"],
            createFromJS.SizeRecord({
              width: change.width,
              height: change.height
            })
          );
        });
      });
    }
    case actionTypes.SET_LAYOUT_SINGLE_VIEW: {
      return state.filter((v, id) => id === activeViewId);
    }
    case actionTypes.SET_LAYOUT_TWO_VIEW_STACK:
    case actionTypes.SET_LAYOUT_TWO_VIEW_SIDE:
      return setTwoViewsLayout(state, activeViewId, action.viewId, activeView, prevGlobalState).map(v =>
        v.setIn(["size", "height"], 1 * Dimensions.MIN_WORKVIEW_HEIGHT)
      );
    case actionTypes.SET_LAYOUT_TWO_VIEW_STACK_THIRDS: {
      return setLayoutTwoViewStackThirds(state, activeViewId, activeView, action.viewId, prevGlobalState);
    }
    case actionTypes.SWITCH_VIEW_TYPE: {
      return switchViewType(state, targetViewId, action.viewType, prevGlobalState, action.mindmapId);
    }
    case actionTypes.TOGGLE_RELATIONSHIP_COLUMN:
      return toggleRelationshipColumn(state, action, prevGlobalState);
    case actionTypes.TOGGLE_SPREADSHEET_COLUMN:
      return toggleSpreadsheetColumn(state, action, prevGlobalState);
    case actionTypes.RESIZE_SPREADSHEET_COLUMN:
      return resizeSpreadsheetColumn(state, action, prevGlobalState);
    case actionTypes.MOVE_SPREADSHEET_COLUMN:
      return moveSpreadsheetColumn(state, action, prevGlobalState);
    case actionTypes.SET_SPREADSHEET_SORT_BY:
      return sortSpreadsheetColumn(state, targetViewId, action);
    case actionTypes.TOGGLE_OUTLINE_COLUMN:
      return toggleOutlineColumn(state, action, prevGlobalState);
    case actionTypes.RESIZE_OUTLINE_COLUMN:
      return resizeOutlineColumn(state, action, prevGlobalState);
    case actionTypes.MOVE_OUTLINE_COLUMN:
      return moveOutlineColumn(state, action, prevGlobalState);
    case actionTypes.TOGGLE_SUBWAY_THREAD:
      return toggleSubwayThread(state, action, prevGlobalState);
    case actionTypes.DEFAULT_SCROLL_POSITION_TO_TIMESTAMP:
      return defaultScrollPositionToTimestamp(state, action.timestamp, prevGlobalState);
    case actionTypes.RECORD_SCROLL_AND_ZOOM:
      return recordScrollAndZoom(state, action.viewIds, action.scrollPosition, action.zoom, prevGlobalState);
    case actionTypes.MOVE_TIMELINE_SCROLL_POSITIONS:
      return moveTimelineScrollPositions(state, action.seconds);
    case actionTypes.CHANGE_ZOOM_SLIDER:
      return changeZoomSlider(state, action, targetViewId, prevGlobalState);
    case actionTypes.TOGGLE_TIMELINE_GROUP:
      return toggleTimelineGroup(state, action, prevGlobalState);
    case actionTypes.CLEAR_TIMELINE_GROUPS:
      return clearTimelineGroups(state, action, prevGlobalState);
    case timelineViewSettingsActionTypes.CHANGE_ZOOM_FROM:
      return changeZoomLimits(
        state,
        action.zoomFrom,
        prevGlobalState.definitions.timelineViewSettings.zoomTo,
        prevGlobalState
      );
    case timelineViewSettingsActionTypes.CHANGE_ZOOM_TO:
      return changeZoomLimits(
        state,
        prevGlobalState.definitions.timelineViewSettings.zoomFrom,
        action.zoomTo,
        prevGlobalState
      );
    case actionTypes.TOGGLE_EXPAND_SUPER_ITEM:
      return state.updateIn([targetViewId, "expandedItems"], expanded => {
        let newExpanded = expanded;
        action.itemIds.forEach(id => {
          newExpanded = action.isExpanded
            ? newExpanded.contains(action.typeId)
              ? newExpanded
              : newExpanded.push(id)
            : newExpanded.filter(t => t !== id);
        });
        return newExpanded;
      });
    default:
      return state;
  }
};

export default byIdReadonly;
